import { IConsequence, IConsquenceRecord, IRisikoSection } from "@schemas/issueBoardSchema";
import { IIssueLogs } from "@schemas/issueSchema";
import { IOtherAttributeConfigApi, IOtherAttributeConfigItemProps } from "@schemas/otherAttributesSchema";
import React from "react";
import { isDateValidFrontend } from "../../app/routes/admin/selectors";
import { ISSUE_COLUMN_LABELS } from "../../app/routes/issuesApp/issueConstants";
import { formatShortDate } from "../../mc-shared/sharedUtils";

const isNumeric = (v: string) => /^-?\d+(?:[.,]\d+)?$/.test(v.trim());
const isBooleanWord = (v: string) => ["true", "false"].includes(v.trim().toLowerCase());
const toBoolean = (v: string | boolean) => (typeof v === "boolean" ? v : v.trim().toLowerCase() === "true");

const isEmptyLike = (v: unknown) =>
  v == null ||
  (typeof v === "string" &&
    (v.trim() === "" || v.trim().toLowerCase() === "undefined" || v.trim().toLowerCase() === "null"));

const tryParseJson = (v: string): [boolean, any] => {
  try {
    return [true, JSON.parse(v)];
  } catch {
    return [false, null];
  }
};

// Try extract key (A, B, C...) from dataField (otherAttributes)
const extractOtherAttrKey = (df?: string): string | null => {
  if (!df) return null;
  const m =
    df.match(/^otherAttributesData\.(\w+)$/) ||
    df.match(/^otherAttributesData\["(.+?)"\]$/) ||
    df.match(/^otherAttributesData\['(.+?)'\]$/);
  return m ? m[1] : null;
};

/* ======== helpers for Risiko ======== */
const isRisikoProbabilityDf = (df?: string) =>
  !!df && (df === "risiko.probability" || df === `risiko["probability"]` || df === `risiko['probability']`);

const extractRisikoConsequenceCategory = (df?: string): string | null => {
  if (!df) return null;
  const m =
    df.match(/^risiko\.consequence\.(\w+)$/) ||
    df.match(/^risiko\.consequence\["(.+?)"\]$/) ||
    df.match(/^risiko\.consequence\['(.+?)'\]$/);
  return m ? m[1] : null;
};

// map column title
export const resolveColumnTitle = (
  dataField?: string,
  fallbackFromRow?: string,
  otherAttrConf?: IOtherAttributeConfigApi,
  risikoConf?: IRisikoSection,
): string => {
  // otherAttributes
  const keyFromDf = extractOtherAttrKey(dataField);
  if (keyFromDf && otherAttrConf?.[keyFromDf]?.label) return otherAttrConf[keyFromDf].label;
  if (dataField && otherAttrConf?.[dataField]?.label) return otherAttrConf[dataField].label;

  // Risiko
  if (isRisikoProbabilityDf(dataField)) {
    return ISSUE_COLUMN_LABELS["risiko.probability"] || "Sannsynlighet";
  }
  const rk = extractRisikoConsequenceCategory(dataField);
  if (rk) {
    const catName = risikoConf?.evaluationCategoriesConfig?.[rk]?.name ?? rk;
    return `Risiko - ${catName}`;
  }

  // ISSUE constants
  if (dataField && ISSUE_COLUMN_LABELS[dataField]) {
    return ISSUE_COLUMN_LABELS[dataField];
  }

  return fallbackFromRow || dataField || "";
};

type IDynMeta = {
  key?: string;
  conf?: IOtherAttributeConfigItemProps;
};

export type IRowData = IIssueLogs & {
  _displayColumnName: string;
  _dynMeta?: IDynMeta;
};

// map type/alternative for dynamic attributes + Risiko DROPDOWN
export const resolveDynamicMeta = (
  dataField?: string,
  colName?: string,
  otherAttrConf?: IOtherAttributeConfigApi,
  risikoConf?: IRisikoSection,
): IDynMeta => {
  // ----- Risiko as DROPDOWN,reuse DynamicValueRenderer -----
  if (isRisikoProbabilityDf(dataField)) {
    const opts =
      Object.entries(risikoConf?.probabilites ?? {}).map(([k, v]) => ({
        value: k, // match stored key ("A", "B", ...)
        description: v?.name ?? "",
      })) ?? [];
    const conf = {
      typeOfValue: "DROPDOWN",
      allowMultipleSelectionsInDropdown: false,
      tableOptions: opts,
    } as IOtherAttributeConfigItemProps;
    return { key: "risiko.probability", conf };
  }
  const rk = extractRisikoConsequenceCategory(dataField);
  if (rk) {
    const consequences = (risikoConf?.evaluationCategoriesConfig?.[rk]?.consequences as IConsquenceRecord) ?? {};
    const opts = (Object.entries(consequences) as [string, IConsequence][]).map(([k, v]) => ({
      value: k,
      description: v?.name ?? "",
    }));
    const conf = {
      typeOfValue: "DROPDOWN",
      allowMultipleSelectionsInDropdown: false,
      tableOptions: opts,
    } as IOtherAttributeConfigItemProps;
    return { key: `risiko.consequence.${rk}`, conf };
  }

  // ----- otherAttributes -----
  const keyFromDf = extractOtherAttrKey(dataField);
  if (keyFromDf && otherAttrConf?.[keyFromDf]) return { key: keyFromDf, conf: otherAttrConf[keyFromDf] };
  if (dataField && otherAttrConf?.[dataField]) return { key: dataField, conf: otherAttrConf[dataField] };
  if (colName && otherAttrConf?.[colName]) return { key: colName, conf: otherAttrConf[colName] };

  return { key: undefined as string | undefined, conf: undefined as IOtherAttributeConfigItemProps | undefined };
};

// --------- Renderers ----------
const JsonBlock: React.FC<{ children: string }> = ({ children }) => (
  <pre
    style={{
      margin: 0,
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
      fontSize: 12,
      lineHeight: 1.4,
    }}
  >
    {children}
  </pre>
);

// Generic fallback-renderer (for all non-dynamic fields)
const GenericValueRenderer: React.FC<{ value: unknown }> = ({ value }) => {
  if (isEmptyLike(value)) return <span>-</span>;

  if (typeof value === "string") {
    const raw = value.trim();

    if (isDateValidFrontend(raw)) return <span>{formatShortDate(raw)}</span>;
    if (isBooleanWord(raw)) return <span>{toBoolean(raw) ? "Ja" : "Nei"}</span>;
    if (isNumeric(raw)) return <span>{raw.replace(",", ".")}</span>;

    if ((raw.startsWith("{") && raw.endsWith("}")) || (raw.startsWith("[") && raw.endsWith("]"))) {
      const [ok, parsed] = tryParseJson(raw);
      if (ok) {
        if (Array.isArray(parsed) && parsed.every((x) => typeof x === "string")) {
          return <div className="d-flex flex-wrap">{parsed.join(", ")}</div>;
        }
        return <JsonBlock>{JSON.stringify(parsed, null, 2)}</JsonBlock>;
      }
    }

    return <>{raw}</>;
  }

  if (typeof value === "boolean") return <span>{value ? "Ja" : "Nei"}</span>;
  if (typeof value === "number") return <span>{String(value)}</span>;

  return <JsonBlock>{JSON.stringify(value, null, 2)}</JsonBlock>;
};

// Dynamic renderer based on typeOfValue (reused for Risiko as DROPDOWN)
export const DynamicValueRenderer: React.FC<{
  value: unknown;
  meta?: { key?: string; conf?: IOtherAttributeConfigItemProps };
}> = ({ value, meta }) => {
  const type = meta?.conf?.typeOfValue;

  if (!type) {
    return <GenericValueRenderer value={value} />;
  }

  if (isEmptyLike(value)) return <span>-</span>;

  const rawStr = typeof value === "string" ? value.trim() : value;

  switch (type) {
    case "DATE": {
      const s = String(rawStr);
      return <span>{isDateValidFrontend(s) ? formatShortDate(s) : s}</span>;
    }
    case "BOOLEAN": {
      const b =
        typeof rawStr === "boolean" ? rawStr : isBooleanWord(String(rawStr)) ? toBoolean(String(rawStr)) : false;
      return <span>{b ? "Ja" : "Nei"}</span>;
    }
    case "NUMBER": {
      const s = String(rawStr).replace(",", ".");
      return <span>{isNumeric(s) ? s : String(rawStr)}</span>;
    }
    case "DROPDOWN": {
      const conf = meta?.conf;
      const allowMulti = !!conf?.allowMultipleSelectionsInDropdown;
      const opts = conf?.tableOptions || [];

      const toDisplay = (val: string) => {
        const hit = opts.find((o) => String(o.value) === String(val));
        if (hit?.description) return `${hit.description}`;
        return hit?.value ?? val;
      };

      if (allowMulti) {
        let arr: string[] = [];
        if (Array.isArray(rawStr)) arr = rawStr as string[];
        else if (typeof rawStr === "string") {
          const [ok, parsed] = tryParseJson(rawStr);
          if (ok && Array.isArray(parsed)) arr = parsed;
          else arr = [rawStr];
        } else {
          arr = [String(rawStr)];
        }

        return (
          <div className="d-flex flex-wrap">
            {arr.map((v) => {
              const disp = toDisplay(v);
              return <React.Fragment key={v}>{String(disp).split(",").join(", ")}</React.Fragment>;
            })}
          </div>
        );
      }

      const strVal = Array.isArray(rawStr) ? String(rawStr[0]) : String(rawStr);
      return <span>{toDisplay(strVal)}</span>;
    }
    case "MULTILINESTRING": {
      return <span style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.5 }}>{String(rawStr)}</span>;
    }
    case "STRING":
    default: {
      return <span>{String(rawStr)}</span>;
    }
  }
};
